import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, ExternalLink, Smartphone, Globe } from "lucide-react";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEB_APP_URL = process.env.REACT_APP_WEB_APP_URL || "https://app.muft.com.br";
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "https://play.google.com/store/apps/details?id=com.muft.app";
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || "https://apps.apple.com/app/muft-gestao/id123456789";

const MAX_ATTEMPTS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | pending | error

  const poll = useCallback(async (attempt) => {
    if (!sessionId) return setState("error");
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid" || data.status === "completed") {
        setState("success");
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState("pending");
        return;
      }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      if (attempt >= MAX_ATTEMPTS) setState("error");
      else setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[radial-gradient(ellipse_120%_90%_at_50%_-10%,#2b1494_0%,#220F86_45%,#0d0640_100%)] px-6 py-12 text-center" data-testid="payment-success-page">
      <div className="mb-8"><Logo /></div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-navy-800/70 p-8 md:p-10 backdrop-blur-md shadow-2xl"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto mb-5 h-14 w-14 animate-spin text-sky" />
            <h1 className="text-2xl font-extrabold text-white">Confirmando sua assinatura...</h1>
            <p className="mt-3 text-sm text-white/60">Só um instante enquanto validamos os detalhes do seu plano.</p>
          </>
        )}
        {state === "success" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20 ring-8 ring-emerald-500/10"
            >
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </motion.div>
            <h1 className="text-2xl font-extrabold text-white md:text-3xl">Assinatura confirmada!</h1>
            <p className="mt-3 text-sm text-white/70">
              Seu período de teste de 7 dias grátis está ativo. Acesse o sistema agora mesmo pelo navegador ou instale o aplicativo no seu smartphone.
            </p>

            {/* Redirection Action Buttons */}
            <div className="mt-8 flex flex-col gap-3.5 sm:flex-row">
              <a
                href={WEB_APP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="go-to-web-app"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-navy-700 shadow-lg transition-transform hover:scale-[1.03] hover:bg-sky-bright"
              >
                <Globe className="h-4 w-4" /> Acessar App Web <ExternalLink className="h-3.5 w-3.5 opacity-60" />
              </a>

              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="download-mobile-app"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-bold text-white transition-all hover:bg-white/20 hover:scale-[1.03]"
              >
                <Smartphone className="h-4 w-4" /> App Mobile
              </a>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto mb-5 h-14 w-14 text-sky" />
            <h1 className="text-2xl font-extrabold text-white">Pagamento em processamento</h1>
            <p className="mt-3 text-sm text-white/60">Estamos finalizando a confirmação junto ao emissor. Você receberá um e-mail assim que estiver tudo pronto.</p>
            <a
              href={WEB_APP_URL}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-navy-700"
            >
              Ir para a tela de Login <ArrowLeft className="h-4 w-4 rotate-180" />
            </a>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto mb-5 h-14 w-14 text-red-400" />
            <h1 className="text-2xl font-extrabold text-white">Transação não localizada</h1>
            <p className="mt-3 text-sm text-white/60">Se você concluiu o pagamento, aguarde alguns instantes ou entre em contato com nosso suporte.</p>
          </>
        )}

        <div className="mt-8 border-t border-white/10 pt-6">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-xs font-semibold text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar para a página principal
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
